import { Link, useParams, Navigate } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { COMPANY, SERVICES } from '../mock/data';
import { CITY_BY_SLUG, SERVICE_SLUGS } from '../data/locations';

export default function ServiceCityPage() {
  const { serviceSlug, citySlug } = useParams();
  const city = CITY_BY_SLUG[citySlug];
  const svc = SERVICE_SLUGS[serviceSlug];
  if (!city || !svc) return <Navigate to="/" replace />;

  const svcData = SERVICES.find((s) => s.title.toLowerCase().includes(svc.service.toLowerCase().split(' ')[0])) || SERVICES[0];
  const canonical = `https://betterfenceanddeck.org/services/${serviceSlug}/${citySlug}`;
  const title = `${svc.title} in ${city.fullName} | Better Fence & Deck`;
  const description = `Professional ${svc.keyword} in ${city.fullName}. Free written estimates, insured crew, quality guaranteed. Call ${COMPANY.phone}.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: svc.title,
    provider: { '@type': 'LocalBusiness', name: 'Better Fence & Deck', telephone: '+1-318-344-7190' },
    areaServed: { '@type': 'City', name: city.fullName },
    url: canonical,
  };

  return (
    <div className="bg-ink text-cream min-h-screen">
      <SEO title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <Navbar />

      <section className="pt-40 pb-16 md:pb-24 bg-ink relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={svcData.image} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-bold">
            {city.fullName} · {svc.title}
          </div>
          <h1 className="mt-4 font-display uppercase text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {svc.title} <br className="hidden md:block"/> in <span className="text-gold">{city.name}, {city.state}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-cream/80 text-lg leading-relaxed">
            Looking for a trusted contractor for {svc.keyword} in {city.fullName}? Better Fence & Deck is Shreveport-based, fully insured, and installs across the ArkLaTex — including {city.name}. Every quote is free and in writing.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#quote" className="btn-primary">Get Free Quote <ArrowRight className="w-4 h-4" /></a>
            <a href={`tel:${COMPANY.phoneRaw}`} className="btn-ghost"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ink-soft border-y border-white/5">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="eyebrow">About this service</div>
          <h2 className="section-title">{svc.title} done right in {city.name}.</h2>
          <p className="section-lede">{svcData.blurb}</p>
          <ul className="mt-6 space-y-3">
            {svcData.features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-cream/80">
                <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <div className="mt-6 inline-block px-4 py-2 rounded-md bg-gold text-ink font-bold text-sm uppercase tracking-widest">
            Starting at {svcData.from}
          </div>
        </div>
      </section>

      <section id="quote" className="py-16 md:py-24 bg-ink">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <div className="eyebrow justify-center inline-flex">Ready to build?</div>
          <h2 className="section-title">Free estimate for your {city.name} project.</h2>
          <p className="section-lede mx-auto">Reach us at {COMPANY.phone} or fill out the online quote form. We typically respond within an hour during business hours.</p>
          <div className="mt-6 flex justify-center gap-3">
            <a href={`tel:${COMPANY.phoneRaw}`} className="btn-primary"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
            <Link to="/#quote" className="btn-outline">Fill Quote Form</Link>
          </div>
          <div className="mt-8">
            <Link to={`/fence-contractor/${city.slug}`} className="text-gold hover:text-gold-light text-sm underline underline-offset-4">
              &larr; See all fence services in {city.name}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
